"use client";

import { useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { countryGroups } from "@/data/countries";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

function SampleRequestFormContent() {
  const searchParams = useSearchParams();
  const { locale } = useLocale();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    products: [] as string[],
    sampleQuantity: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const initialProduct = searchParams.get("product");
    if (initialProduct) {
      setFormData((prev) => ({ ...prev, products: [initialProduct] }));
    }
  }, [searchParams]);

  const categories = ["Powder", "Fresh Herbs", "Extracts", "Beans", "Leaves"];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { selectedOptions } = e.target;
    const selectedProducts = Array.from(selectedOptions).map((option) => option.value);
    setFormData((prev) => ({ ...prev, products: selectedProducts }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch("/api/sample-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit sample request");
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting sample request:", error);
      alert("Failed to submit sample request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-2xl mx-auto bg-brand-50 border border-brand-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-brand-800 mb-4">{t(locale, "sample.submittedTitle")}</h2>
          <p className="text-brand-700 mb-4">
            {t(locale, "sample.submittedBody")}
          </p>
          <div className="text-left bg-white rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700 mb-2"><strong>{t(locale, "sample.important")}</strong></p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>{t(locale, "sample.feeQuote")}</li>
              <li>{t(locale, "sample.shippingSeparate")}</li>
              <li>{t(locale, "sample.contactPayment")}</li>
              <li>{t(locale, "sample.deliveryTime")}</li>
            </ul>
          </div>
          <a
            href="/sample-request"
            className="inline-block bg-brand-600 text-white px-6 py-2 rounded-lg hover:bg-brand-700 transition-colors"
          >
            {t(locale, "sample.requestAnother")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-24 pb-12">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-display text-brand-900 mb-2">{t(locale, "sample.title")}</h1>
          <p className="text-gray-600">
            {t(locale, "sample.subtitle")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-8 border border-gray-100">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "sample.fullName")}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "sample.emailLabel")}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "sample.companyLabel")}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "sample.countryLabel")}
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  <option value="">{t(locale, "sample.selectCountry")}</option>
                  {Object.entries(countryGroups).map(([region, regionCountries]) => (
                    <optgroup key={region} label={region}>
                      {regionCountries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <label htmlFor="products" className="block text-sm font-medium text-gray-700 mb-2">
                  {t(locale, "sample.sampleProducts")}
                </label>
              <select
                id="products"
                name="products"
                value={formData.products}
                onChange={handleProductChange}
                multiple
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500 h-32 mb-3"
                required
              >
                {categories.map((category) => (
                  <optgroup key={category} label={category}>
                    <option value="powder">{t(locale, "product.powder")}</option>
                    <option value="fresh">{t(locale, "product.fresh")}</option>
                    <option value="extract">{t(locale, "product.extract")}</option>
                    <option value="bean">{t(locale, "product.bean")}</option>
                    <option value="leaf">{t(locale, "product.leaf")}</option>
                  </optgroup>
                ))}
              </select>
                <p className="text-sm text-gray-500 hidden sm:block">
                  {t(locale, "sample.helpDesktop")}
                </p>
                <p className="text-sm text-gray-500 sm:hidden">
                  {t(locale, "sample.helpMobile")}
                </p>
            </div>

            <div>
              <label htmlFor="sampleQuantity" className="block text-sm font-medium text-gray-700 mb-2">
                {t(locale, "sample.qtyLabel")}
              </label>
              <input
                type="text"
                id="sampleQuantity"
                name="sampleQuantity"
                value={formData.sampleQuantity}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder={t(locale, "sample.qtyPlaceholder")}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t(locale, "sample.requirementsLabel")}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder={t(locale, "sample.requirementsPlaceholder")}
              />
            </div>

            <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
              <h4 className="font-semibold text-brand-900 mb-2">{t(locale, "sample.policy")}</h4>
              <ul className="text-sm text-brand-700 list-disc list-inside space-y-1">
                <li>{t(locale, "sample.policy1")}</li>
                <li>{t(locale, "sample.policy2")}</li>
                <li>{t(locale, "sample.policy3")}</li>
                <li>{t(locale, "sample.policy4")}</li>
                <li>{t(locale, "sample.policy5")}</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg font-medium transition-colors ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-brand-600 text-white hover:bg-brand-700"
              }`}
            >
              {loading ? t(locale, "sample.submitting") : t(locale, "sample.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SampleRequestForm() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 pt-24 pb-12 text-center">Loading...</div>}>
      <SampleRequestFormContent />
    </Suspense>
  );
}
